import type { ImportField } from '@/classes/ImportField'
import { formatDate, isDefined, sanitizeAlphaNumeric } from '@/composables/utils'
import { EndPoint } from '@/enums/EndPoint'
import { ImportBooleanError } from '@/errors/ImportBooleanError'
import { ImportDateError } from '@/errors/ImportDateError'
import { ImportFormFactorError } from '@/errors/ImportFormFactorError'
import { ImportInvalidValueError } from '@/errors/ImportInvalidValueError'
import { ImportLengthError } from '@/errors/ImportLengthError'
import { ImportNumberError } from '@/errors/ImportNumberError'
import { ImportValueRequiredError } from '@/errors/ImportValueRequiredError'
import type { IImportDefinition } from '@/interfaces/IImportDefinition'
import type { ConvertFunction } from '@/types/ConvertFunction'
import { isNumber } from 'highcharts'
import { type Composer } from 'vue-i18n'
import { getImportDefinition as getMedicalClearanceImportDefinition } from './ImportClearances'
import { getImportDefinition as getCustomFieldImportDefinition } from './ImportCustomFields'
import { getImportDefinition as getDailyChecksImportDefinition } from './ImportDailyChecks'
import { getImportDefinition as getDeviceImportDefinition } from './ImportDevices'
import { getImportDefinition as getFitTestImportDefinition } from './ImportFitTests'
import { getImportDefinition as getMaskImportDefinition } from './ImportMasks'
import { getImportDefinition as getPeopleImportDefinition } from './ImportPeople'

const _importDefinitions = new Map<EndPoint, IImportDefinition>()

const _initialize = () => {
  _importDefinitions.set(EndPoint.CustomFields, getCustomFieldImportDefinition())
  _importDefinitions.set(EndPoint.People, getPeopleImportDefinition())
  _importDefinitions.set(EndPoint.FitTests, getFitTestImportDefinition())
  _importDefinitions.set(EndPoint.MedicalClearances, getMedicalClearanceImportDefinition())
  _importDefinitions.set(EndPoint.Masks, getMaskImportDefinition())
  _importDefinitions.set(EndPoint.Devices, getDeviceImportDefinition())
  _importDefinitions.set(EndPoint.DailyChecks, getDailyChecksImportDefinition())
}

export const getImportDefinitions = (): Array<IImportDefinition> => {
  if (_importDefinitions.size === 0) {
    _initialize()
  }
  return [..._importDefinitions.values()]
}

/**
 * Displ;ay dates in readable format.
 * @param value A valid ISO date string.
 * @returns
 */
export const displayDate = (value: string | null): string => {
  if (!isDefined(value)) {
    return ''
  }
  return formatDate(value, true)
}

/**
 * Converts the protocol algorithm to a numeric value.
 * @param value A string that represents an algorithm.
 * @param importField An ImportField instance.
 * @param i18n An i18n instance.
 * @returns
 */
export const toAlgorithm = (
  value: string,
  importField: ImportField,
  i18n: Composer<{}, {}, {}, string, never, string>
): number | null => {
  if (!isDefined(value)) {
    if (isDefined(importField.defaultValue)) {
      return importField.defaultValue
    }
    if (importField.required) {
      throw new ImportValueRequiredError(
        i18n.t('errors.importTable.messages.valueRequired', { fieldTitle: importField.title })
      )
    }
    return null
  }

  switch (value.toLowerCase()) {
    case 'original':
    case '0': {
      return 0
    }
    case 'algorithm1':
    case '1': {
      return 1
    }
    case 'qlft':
    case '2': {
      return 2
    }
    default: {
      throw new ImportInvalidValueError(
        i18n.t('errors.importTable.messages.invalidAlgorithm', { fieldTitle: importField.title })
      )
    }
  }
}

/**
 * Converts an import string value to a boolean.
 * @param value A text string that represents a boolean [TRUE, FALSE, 0, 1, YES, NO].
 * @param importField An ImportField instance.
 * @param i18n An i18n instance.
 * @returns
 */
export const toBoolean = (
  value: string,
  importField: ImportField,
  i18n: Composer<{}, {}, {}, string, never, string>
): boolean | null => {
  if (!isDefined(value)) {
    if (isDefined(importField.defaultValue)) {
      return importField.defaultValue
    }
    if (importField.required) {
      throw new ImportValueRequiredError(
        i18n.t('errors.importTable.messages.valueRequired', { fieldTitle: importField.title })
      )
    }
    return null
  }

  const sanitizedValue = sanitizeAlphaNumeric(value)
  switch (sanitizedValue) {
    case 'true':
    case 'yes':
    case '1': {
      return true
    }
    case 'false':
    case 'no':
    case '0': {
      return false
    }
    default: {
      throw new ImportBooleanError(
        i18n.t('errors.importTable.messages.invalidBoolean', {
          fieldTitle: importField.title,
          value
        })
      )
    }
  }
}

/**
 * Converts an import string value to a proper form factor value.
 * @param value A text string that represents a form factor [1, elastomeric, fullfacehalfface, 0, filtering, filteringfacepiece].
 * @param importField An ImportField instance.
 * @param i18n An i18n instance.
 * @returns
 */
export const toFormFactor = (
  value: string,
  importField: ImportField,
  i18n: Composer<{}, {}, {}, string, never, string>
): string | null => {
  if (!isDefined(value)) {
    if (isDefined(importField.defaultValue)) {
      return importField.defaultValue
    }
    if (importField.required) {
      throw new ImportValueRequiredError(
        i18n.t('errors.importTable.messages.valueRequired', { fieldTitle: importField.title })
      )
    }
    return null
  }

  const sanitizedValue = sanitizeAlphaNumeric(value)
  switch (sanitizedValue) {
    case '1':
    case 'elastomeric':
    case 'fullfacehalfface': {
      return '1'
    }
    case '0':
    case 'filtering':
    case 'filteringfacepiece': {
      return '0'
    }
    default: {
      throw new ImportFormFactorError(
        i18n.t('errors.importTable.messages.invalidFormFactor', {
          fieldTitle: importField.title,
          value
        })
      )
    }
  }
}

/**
 * Converts an import string value to an ISODate string.
 * @param value A string that represents a date.
 * @param importField An ImportField instance.
 * @param i18n An i18n instance.
 * @returns
 */
export const toISODate = (
  value: string,
  importField: ImportField,
  i18n: Composer<{}, {}, {}, string, never, string>
): string | null => {
  if (!isDefined(value)) {
    if (isDefined(importField.defaultValue)) {
      return importField.defaultValue
    }
    if (importField.required) {
      throw new ImportValueRequiredError(
        i18n.t('errors.importTable.messages.valueRequired', { fieldTitle: importField.title })
      )
    }
    return null
  }

  const dateValue = Date.parse(value)
  if (isNaN(dateValue)) {
    throw new ImportDateError(
      i18n.t('errors.importTable.messages.invalidDate', { fieldTitle: importField.title, value })
    )
  }
  return new Date(value).toISOString()
}

/**
 * Converts an import string value to an ISODate string. Requires the date to be in the past.
 * @param value A string that represents a date.
 * @param importField An ImportField instance.
 * @param i18n An i18n instance.
 * @returns
 */
export const toISODateInPast = (
  value: string,
  importField: ImportField,
  i18n: Composer<{}, {}, {}, string, never, string>
): string | null => {
  const isoDate = toISODate(value, importField, i18n)
  if (!isoDate) {
    return null
  }
  const dateValue = new Date(isoDate)
  if (dateValue > new Date()) {
    throw new ImportDateError(
      i18n.t('errors.importTable.messages.invalidDateInPast', {
        fieldTitle: importField.title,
        value
      })
    )
  }
  return isoDate
}

/**
 * Converts an import string to a numeric value.
 * @param value A string that represents a number.
 * @param importField An ImportField instance.
 * @param i18n An i18n instance.
 * @returns
 */
export const toNumber = (
  value: string,
  importField: ImportField,
  i18n: Composer<{}, {}, {}, string, never, string>
): number | null => {
  if (!isDefined(value)) {
    if (isDefined(importField.defaultValue)) {
      return importField.defaultValue
    }
    if (importField.required) {
      throw new ImportValueRequiredError(
        i18n.t('errors.importTable.messages.valueRequired', { fieldTitle: importField.title })
      )
    }
    return null
  }

  const truthy = ['yes', 'true']
  const falsy = ['no', 'false']
  let numericValue
  if (truthy.indexOf(value.toLowerCase().trim()) >= 0) {
    numericValue = 1
  } else if (falsy.indexOf(value.toLowerCase().trim()) >= 0) {
    numericValue = 0
  } else {
    numericValue = Number.parseFloat(value)
  }
  if (isNaN(numericValue)) {
    throw new ImportNumberError(
      i18n.t('errors.importTable.messages.invalidNumber', { fieldTitle: importField.title, value })
    )
  }
  return numericValue
}

/**
 * Converts an id provided by CustomFields to an index. Used only by custom fields.
 * @param value A string that represents a number.
 * @param importField An ImportField instance.
 * @param i18n An i18n instance.
 * @returns
 */
export const toIndex = (
  value: string,
  importField: ImportField,
  i18n: Composer<{}, {}, {}, string, never, string>
): number => {
  const numValue = parseInt(value)
  if (isNumber(numValue)) {
    let calcValue = numValue % 6
    calcValue++
    if (calcValue === 6) {
      calcValue = 0
    }
    return calcValue
  }
  throw new ImportNumberError(
    i18n.t('errors.importTable.messages.invalidNumber', { fieldTitle: importField.title, value })
  )
}

/**
 * Converts an overall pass value to a number.  PASS = 1, FAIL = 0.
 * @param value A string that represents a number.
 * @param importField An ImportField instance.
 * @param i18n An i18n instance.
 * @returns
 */
export const toPassFail = (
  value: string,
  importField: ImportField,
  i18n: Composer<{}, {}, {}, string, never, string>
): number | null => {
  if (!isDefined(value)) {
    if (isDefined(importField.defaultValue)) {
      return importField.defaultValue
    }
    if (importField.required) {
      throw new ImportValueRequiredError(
        i18n.t('errors.importTable.messages.valueRequired', { fieldTitle: importField.title })
      )
    }
    return null
  }

  switch (value.toLowerCase()) {
    case 'pass':
    case '1':
    case 'yes':
    case 'true': {
      return 1
    }
    case 'fail':
    case '0':
    case 'no':
    case 'false': {
      return 0
    }
    default: {
      throw new ImportInvalidValueError(
        i18n.t('errors.importTable.messages.invalidPassFail', { fieldTitle: importField.title })
      )
    }
  }
}

/**
 * Converts an import string to a proper database string.
 * @param value A simple string value.
 * @param importField An ImportField instance.
 * @param i18n An i18n instance.
 * @returns
 */
export const toText = (maxLength: number): ConvertFunction => {
  return (
    value: string,
    importField: ImportField,
    i18n: Composer<{}, {}, {}, string, never, string>
  ): string | null => {
    if (!isDefined(value)) {
      if (isDefined(importField.defaultValue) || importField.defaultValue === '') {
        return importField.defaultValue
      }
      if (importField.required) {
        throw new ImportValueRequiredError(
          i18n.t('errors.importTable.messages.valueRequired', { fieldTitle: importField.title })
        )
      }
      return null
    }

    if (value.length > maxLength) {
      throw new ImportLengthError(
        i18n.t('errors.importTable.messages.invalidString', {
          fieldTitle: importField.title,
          maxLength,
          value
        })
      )
    }
    return value
  }
}
